/**
 * Research logging utility
 * Handles all research data collection in a non-blocking way
 */

import { useStore } from '../store/useStore'
import { computeCodeDelta, classifyPrompt } from './codeAnalysis'

/**
 * Logs an AI iteration to the research database
 * @param {string} participantId - UUID of participant
 * @param {string} sessionId - UUID of session
 * @param {number} iterationIndex - Index of this iteration
 * @param {string} promptText - User's prompt
 * @param {string} responseText - AI's response
 * @param {string} thinkingText - AI's thinking process
 * @param {object} sketchBefore - Sketch state before prompt
 * @param {string} provider - AI provider
 * @param {string} model - AI model
 * @param {number} timeToPromptSec - Time spent typing prompt
 * @returns {Promise<string>} - Returns iteration ID
 */
export async function logIteration({
  participantId,
  sessionId,
  iterationIndex,
  promptText,
  responseText,
  thinkingText,
  sketchBefore,
  provider,
  model,
  timeToPromptSec,
  recovered = false,
  aiJobId = null
}) {
  // Don't block UI if logging fails
  try {
    // Classify the prompt pattern
    const promptClassification = classifyPrompt(promptText)
    
    const response = await fetch('/api/research-iteration', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'create',
        participantId,
        sessionId,
        iterationIndex,
        promptText,
        responseText,
        thinkingText: thinkingText || '',
        sketchBefore,
        provider,
        model,
        timeToPromptSec,
        promptPattern: promptClassification.primaryType,
        promptWordCount: promptClassification.wordCount,
        hasCodeSnippet: promptClassification.hasCodeSnippet,
        recovered,
        aiJobId
      })
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP ${response.status}`)
    }
    
    const data = await response.json()
    return data.iterationId
  } catch (error) {
    console.warn('[Research] Failed to log iteration:', error.message)
    return null
  }
}

/**
 * Updates an iteration with sketch after and/or feedback
 * @returns {Promise<boolean>} - true if update succeeded
 */
export async function updateIteration({
  iterationId,
  sketchBefore,
  sketchAfter,
  rating,
  ratingComment,
  appliedSuggestion,
  editAfterApply
}) {
  try {
    // Compute code delta if we have both before and after states
    let codeDelta = null
    if (sketchBefore && sketchAfter) {
      codeDelta = computeCodeDelta(sketchBefore, sketchAfter)
    }
    
    const response = await fetch('/api/research-iteration', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'update',
        iterationId,
        sketchAfter,
        rating,
        ratingComment,
        appliedSuggestion,
        editAfterApply,
        linesAdded: codeDelta?.linesAdded,
        linesRemoved: codeDelta?.linesRemoved,
        filesModified: codeDelta?.filesModified ? JSON.stringify(codeDelta.filesModified) : null
      })
    })
    
    return response.ok
  } catch (error) {
    console.warn('[Research] Failed to update iteration:', error.message)
    return false
  }
}

/**
 * Marks a session as ended
 */
export async function endSession(sessionId, reason) {
  try {
    await fetch('/api/research-session-end', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        endedReason: reason
      })
    })
  } catch (error) {
    console.warn('[Research] Failed to end session:', error)
  }
}

/**
 * Captures current sketch state as JSON
 */
export function captureSketchState() {
  const store = useStore.getState()
  return {
    files: store.files.map(f => ({
      name: f.name,
      content: f.content,
      type: f.type
    })),
    activeFile: store.currentFile
  }
}
